import { useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { UserMode } from '@/types/auth.types';
import { BookOpen, Shield, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { PERMISSION_LIST_KEY } from '@/constants/constant-permissions';
import { getAvailableRoute } from '@/constants/constant-permission-route-mapping';

export default function ModeToggle() {
  const { userMode, switchMode, canSwitchMode } = useAuth();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  // Check if user has any permission for each mode
  const { hasAdminPermission, hasLearnerPermission } = useMemo(() => {
    const userPermissions: string[] = JSON.parse(
      sessionStorage.getItem(PERMISSION_LIST_KEY) || '[]',
    );
    return {
      hasAdminPermission: !!getAvailableRoute(userPermissions, '/admin'),
      hasLearnerPermission: !!getAvailableRoute(userPermissions, '/learn'),
    };
  }, []);

  // Only show toggle if user has both roles AND has permissions for both modes
  if (!canSwitchMode() || !hasAdminPermission || !hasLearnerPermission) {
    return null;
  }

  const handleModeSwitch = async (newMode: UserMode) => {
    if (newMode === userMode.currentMode) {
      setShowDropdown(false);
      return;
    }

    setIsSwitching(true);
    setShowDropdown(false);

    // Show loading toast
    const loadingToast = toast.loading(`Switching to ${newMode === 'admin' ? 'Admin' : 'Learner'} mode...`);

    try {
      // Navigate FIRST so the URL matches the new mode
      // (prevents AuthContext URL-sync useEffect from resetting mode back)
      if (newMode === 'admin') {
        navigate('/admin');
      } else {
        navigate('/learn');
      }

      // Then switch mode — URL already matches, so URL-sync won't fight back
      switchMode(newMode);

      toast.dismiss(loadingToast);
      toast.success(`Switched to ${newMode === 'admin' ? 'Admin' : 'Learner'} mode`);
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Failed to switch mode');
      console.error('Mode switch error:', error);
    } finally {
      setIsSwitching(false);
    }
  };

  const getModeIcon = (mode: UserMode) => {
    return mode === 'admin' ? (
      <Shield className="w-4 h-4" />
    ) : (
      <BookOpen className="w-4 h-4" />
    );
  };

  const getModeLabel = (mode: UserMode) => {
    return mode === 'admin' ? 'Admin Mode' : 'Learning Mode';
  };

  const getModeColor = (mode: UserMode) => {
    return mode === 'admin' 
      ? 'bg-blue-100 text-blue-700 border-blue-200' 
      : 'bg-green-100 text-green-700 border-green-200';
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        disabled={isSwitching}
        title={getModeLabel(userMode.currentMode)}
        className={`flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1.5 rounded-lg border transition-all ${
          getModeColor(userMode.currentMode)
        } ${isSwitching ? 'opacity-50 cursor-wait' : 'hover:shadow-md cursor-pointer'}`}
      >
        {isSwitching ? (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : (
          getModeIcon(userMode.currentMode)
        )}
        {/* Label only from lg: on tablets the header has no room and it
            wrapped onto two lines. The icon + colour still show the mode, and
            the dropdown names both modes. */}
        <span className="hidden lg:inline text-sm font-medium whitespace-nowrap">
          {getModeLabel(userMode.currentMode)}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
      </button>

      {showDropdown && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setShowDropdown(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-56 max-w-[calc(100vw-1.5rem)] bg-white rounded-lg shadow-lg border z-20">
            <div className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Switch Mode
              </div>
              
              {/* Admin Mode Option */}
              <button
                onClick={() => handleModeSwitch('admin')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  userMode.currentMode === 'admin'
                    ? 'bg-blue-50 text-blue-700'
                    : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <Shield className="w-5 h-5" />
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium">Admin Mode</p>
                  <p className="text-xs text-gray-500">Manage system & users</p>
                </div>
                {userMode.currentMode === 'admin' && (
                  <div className="w-2 h-2 bg-blue-600 rounded-full" />
                )}
              </button>

              {/* Learner Mode Option */}
              <button
                onClick={() => handleModeSwitch('learner')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  userMode.currentMode === 'learner'
                    ? 'bg-green-50 text-green-700'
                    : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <BookOpen className="w-5 h-5" />
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium">Learning Mode</p>
                  <p className="text-xs text-gray-500">Access your courses</p>
                </div>
                {userMode.currentMode === 'learner' && (
                  <div className="w-2 h-2 bg-green-600 rounded-full" />
                )}
              </button>
            </div>

            <div className="border-t px-3 py-2">
              <p className="text-xs text-gray-500">
                You have access to both admin and learner features
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}